import { gsap } from 'gsap'

import { get } from '../utils/ajax'
import { prefersReducedMotion } from '../utils/pauseWhenOffscreen'
import Text from '../animations/Text'

// Matches the per-word timing in `animations/Text.js`.
const WORD_STAGGER = 65

// `--ease-default` (ease-out-cubic) with `$global-transition` / `$global-transition-slow`.
const EASE = 'power2.out'
const FADE = 0.333
const PULSE = 0.5

// Show "Sending" for at least one full pulse, so a fast response doesn't flash.
const MIN_SENDING = (FADE + PULSE * 2) * 1000

// How long "Sent" stays on the button before it resets.
const RESET_DELAY = 5000

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Submits the newsletter form with fetch. A fresh CSRF token is requested
 * first, because statically cached pages (and pages swapped in by the SPA
 * router) can carry a stale or placeholder `_token`. Without JS, or if the
 * request fails unexpectedly, the form falls back to a regular POST.
 *
 * The button label moves through sending/sent states, and success and error
 * messages replace the microcopy below the button.
 */
export default class NewsletterForm {
  constructor () {
    this.onSubmit = this.onSubmit.bind(this)

    this.create()
  }

  create () {
    this.form = document.querySelector('[data-newsletter-form]')

    if (!this.form) return

    this.submit = this.form.querySelector('[type="submit"]')
    this.message = this.form.querySelector('[data-newsletter-message]')
    this.microcopy = this.message.querySelector('[data-newsletter-microcopy]')
    this.feedback = this.message.querySelector('[data-newsletter-feedback]')
    this.microcopyHTML = this.microcopy.innerHTML

    // Wrap the label so it can fade without fading the button itself.
    this.submitLabel = this.submit.textContent.trim()
    this.label = document.createElement('span')
    this.label.textContent = this.submitLabel
    this.submit.replaceChildren(this.label)

    this.form.addEventListener('submit', this.onSubmit)
  }

  async onSubmit (event) {
    event.preventDefault()

    if (this.form.getAttribute('aria-busy') === 'true') return

    this.setBusy(true)
    this.clear()

    const sending = this.showSending()

    try {
      const [{ response, json }] = await Promise.all([this.send(), wait(MIN_SENDING), sending])

      if (response.ok && json.success) {
        this.form.reset()
        // Closes the keyboard on iOS (e.g. after submitting with "Send"), so it doesn't cover the success message.
        document.activeElement?.blur()
        await this.setLabel(this.form.dataset.sentLabel || this.submitLabel)
        await this.onSuccess()
        this.resetLater()
        return
      }

      if (!json.error) {
        throw new Error(`Unexpected response: ${response.status}`)
      }

      await this.setLabel(this.submitLabel)
      this.showErrors(json.error)
    } catch (error) {
      console.error('Newsletter form:', error)
      this.stopPulse()
      this.submit.textContent = this.submitLabel
      this.form.removeEventListener('submit', this.onSubmit)
      this.form.submit()
      return
    }

    this.setBusy(false)
  }

  async send () {
    const { csrf_token } = JSON.parse(await get('/!/DynamicToken/refresh'))

    // Write it into the form too, so the regular-POST fallback is valid.
    this.form.querySelector('[name="_token"]').value = csrf_token

    const response = await fetch(this.form.action, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: new FormData(this.form)
    })

    return { response, json: await response.json() }
  }

  /**
   * Button label.
   */
  async showSending () {
    await this.fadeLabel(0)

    this.label.textContent = this.form.dataset.sendingLabel || this.submitLabel

    if (prefersReducedMotion()) {
      gsap.set(this.label, { opacity: 1 })
      return
    }

    this.pulse = gsap.fromTo(this.label,
      { opacity: 0 },
      { opacity: 1, duration: PULSE, ease: EASE, yoyo: true, repeat: -1 }
    )
  }

  // Fades the current label out, swaps the text, and fades back in.
  async setLabel (text) {
    this.stopPulse()

    await this.fadeLabel(0)
    this.label.textContent = text
    await this.fadeLabel(1)
  }

  fadeLabel (opacity) {
    return new Promise(resolve => {
      gsap.to(this.label, { opacity, duration: FADE, ease: EASE, overwrite: true, onComplete: resolve })
    })
  }

  stopPulse () {
    if (this.pulse) {
      this.pulse.kill()
      this.pulse = null
    }
  }

  // Returns the button to its original label; the success message stays until the next submission.
  resetLater () {
    setTimeout(async () => {
      await this.setLabel(this.submitLabel)
      this.setBusy(false)
    }, RESET_DELAY)
  }

  /**
   * Messages.
   */
  async onSuccess () {
    await this.hideMicrocopy()

    this.showSuccess(this.form.dataset.successMessage)
  }

  // Hides the microcopy word by word, in reverse (the opposite of `animations/Text.js`).
  hideMicrocopy () {
    const words = this.wrapWords(this.microcopy).reverse()

    this.message.style.minHeight = `${this.message.clientHeight}px`

    return new Promise(resolve => {
      words.forEach((word, index) => {
        setTimeout(() => { word.style.display = 'none' }, WORD_STAGGER * index)
      })

      setTimeout(() => {
        this.microcopy.hidden = true
        resolve()
      }, WORD_STAGGER * words.length)
    })
  }

  // Reveals the message word by word with the same animation as the home hero.
  showSuccess (text) {
    const announcement = document.createElement('span')
    const animated = document.createElement('span')

    announcement.className = 'u-sr-only'
    announcement.textContent = text

    animated.setAttribute('aria-hidden', 'true')
    animated.dataset.animation = 'text'
    animated.textContent = text

    this.feedback.append(announcement, animated)
    this.animation = new Text({ element: animated })
  }

  showErrors (errors) {
    this.microcopy.hidden = true

    Object.entries(errors).forEach(([name, text]) => {
      const input = this.form.querySelector(`[name="${name}"]`)

      if (!input) return

      const error = document.createElement('span')

      error.id = `${input.id}-error`
      error.textContent = text

      this.feedback.append(error, ' ')

      input.setAttribute('aria-invalid', 'true')
      input.setAttribute('aria-describedby', error.id)
    })

    this.form.querySelector('[aria-invalid="true"]')?.focus()
  }

  // Resets the message area to the microcopy and clears any errors.
  clear () {
    if (this.animation) {
      this.animation.destroy()
      this.animation = null
    }

    this.message.style.minHeight = ''
    this.feedback.innerHTML = ''
    this.microcopy.innerHTML = this.microcopyHTML
    this.microcopy.hidden = false

    this.form.querySelectorAll('[aria-invalid]').forEach(input => {
      input.removeAttribute('aria-invalid')
      input.removeAttribute('aria-describedby')
    })
  }

  // Wraps each word of an element's text in a span, leaving other markup (e.g. `<br>`) in place.
  wrapWords (element) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT)
    const nodes = []

    while (walker.nextNode()) nodes.push(walker.currentNode)

    nodes.forEach(node => {
      const fragment = document.createDocumentFragment()

      node.textContent.split(/(\s+)/).forEach(part => {
        if (!part) return

        if (/^\s+$/.test(part)) {
          fragment.append(part)
        } else {
          const span = document.createElement('span')

          span.textContent = part
          fragment.append(span)
        }
      })

      node.replaceWith(fragment)
    })

    return [...element.querySelectorAll('span')]
  }

  setBusy (busy) {
    this.form.setAttribute('aria-busy', busy)
    // aria-disabled (not disabled) keeps keyboard focus on the button; aria-busy blocks resubmits.
    this.submit.setAttribute('aria-disabled', busy)
  }
}
